import {
  PermissionCode,
  createAssetFormSchema,
  toAssetRequest,
  type AssetDetail,
  type CreateAssetFormValues,
} from '@gb-ticket/shared-types';
import { Loader2Icon } from 'lucide-react';
import { type FormEvent, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import { toast } from 'sonner';

import { useVoiceCopy } from '@/platform/voice/use-voice';

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  AssetStatusBadge,
  DateExpiryBadge,
} from '@/products/helpdesk/features/assets/asset-badges';
import { ASSET_NONE, AssetFields } from '@/products/helpdesk/features/assets/asset-fields';
import {
  useAsset,
  useAssetLookups,
  useDeleteAsset,
  useLinkAssetTicket,
  useUnlinkAssetTicket,
  useUpdateAsset,
} from '@/products/helpdesk/features/assets/use-assets';
import { hasPermission } from '@/features/auth/permissions';
import { useMe } from '@/features/auth/use-me';
import { PriorityBadge, StatusBadge } from '@/products/helpdesk/features/tickets/ticket-badges';
import { ApiRequestError } from '@/lib/api';
import { formatDate, formatDateTime } from '@/lib/format';
import { PersonChip } from '@/platform/components/person-chip';

function toForm(asset: AssetDetail): CreateAssetFormValues {
  return {
    name: asset.name,
    assetTypeId: asset.assetType.id,
    status: asset.status,
    inventoryTag: asset.inventoryTag ?? '',
    serialNumber: asset.serialNumber ?? '',
    location: asset.location ?? '',
    departmentId: asset.department?.id ?? ASSET_NONE,
    assignedUserId: asset.assignedUser?.id ?? ASSET_NONE,
    purchaseDate: asset.purchaseDate ?? '',
    warrantyUntil: asset.warrantyUntil ?? '',
    maintenanceUntil: asset.maintenanceUntil ?? '',
    notes: asset.notes ?? '',
  };
}

export function AssetDetailPage() {
  const { assetId } = useParams<{ assetId: string }>();
  const assetQuery = useAsset(assetId);

  if (assetQuery.isPending) {
    return (
      <p className="flex items-center gap-2 text-muted-foreground">
        <Loader2Icon className="size-4 animate-spin" aria-hidden />
        Asset wird geladen …
      </p>
    );
  }

  if (assetQuery.isError || !assetQuery.data) {
    return (
      <section className="space-y-3">
        <h1 className="text-2xl font-bold">Asset nicht gefunden</h1>
        <p className="text-muted-foreground">
          {assetQuery.error instanceof ApiRequestError
            ? assetQuery.error.message
            : 'Das Asset konnte nicht geladen werden.'}
        </p>
        <Button asChild variant="outline">
          <Link to="/helpdesk/assets">Zurück zur Übersicht</Link>
        </Button>
      </section>
    );
  }

  return <AssetDetailBody asset={assetQuery.data} />;
}

function AssetDetailBody({ asset }: { asset: AssetDetail }) {
  const copy = useVoiceCopy();
  const me = useMe();
  const navigate = useNavigate();
  const lookups = useAssetLookups();
  const canWrite = hasPermission(me.data, PermissionCode.ASSETS_WRITE);
  const canDelete = hasPermission(me.data, PermissionCode.ASSETS_DELETE);
  const updateAsset = useUpdateAsset(asset.id);
  const deleteAsset = useDeleteAsset();
  const linkTicket = useLinkAssetTicket(asset.id);
  const unlinkTicket = useUnlinkAssetTicket(asset.id);
  const [values, setValues] = useState<CreateAssetFormValues>(() => toForm(asset));
  const [ticketNumber, setTicketNumber] = useState('');

  function patch(next: Partial<CreateAssetFormValues>) {
    setValues((current) => ({ ...current, ...next }));
  }

  async function onSave(event: FormEvent) {
    event.preventDefault();
    const parsed = createAssetFormSchema.safeParse(values);
    if (!parsed.success) {
      toast.error(parsed.error.issues[0]?.message ?? copy('helpdesk.form.check'));
      return;
    }
    try {
      await updateAsset.mutateAsync(toAssetRequest(parsed.data));
      toast.success('Asset gespeichert.');
    } catch (error) {
      toast.error(error instanceof ApiRequestError ? error.message : 'Speichern fehlgeschlagen.');
    }
  }

  async function onLink() {
    const number = Number.parseInt(ticketNumber.trim(), 10);
    if (!Number.isFinite(number) || number < 1) {
      toast.error('Bitte eine Ticketnummer eingeben.');
      return;
    }
    try {
      await linkTicket.mutateAsync({ ticketNumber: number });
      setTicketNumber('');
      toast.success(`Ticket #${number} verknüpft.`);
    } catch (error) {
      toast.error(error instanceof ApiRequestError ? error.message : 'Verknüpfung fehlgeschlagen.');
    }
  }

  async function onDelete() {
    try {
      await deleteAsset.mutateAsync(asset.id);
      toast.success('Asset gelöscht.');
      await navigate('/helpdesk/assets');
    } catch (error) {
      toast.error(error instanceof ApiRequestError ? error.message : 'Löschen fehlgeschlagen.');
    }
  }

  return (
    <section className="space-y-6">
      <div>
        <Button asChild variant="ghost" size="sm" className="mb-1 -ml-2">
          <Link to="/helpdesk/assets">← Zurück zur Übersicht</Link>
        </Button>
        <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
          <div className="space-y-2">
            <p className="text-sm text-muted-foreground">
              {asset.inventoryTag ?? 'Ohne Inventarnummer'}
            </p>
            <h1 className="text-2xl font-bold tracking-tight">{asset.name}</h1>
            <div className="flex flex-wrap items-center gap-2">
              <AssetStatusBadge status={asset.status} />
              <span className="text-sm text-muted-foreground">{asset.assetType.name}</span>
            </div>
          </div>
        </div>
      </div>

      <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_20rem]">
        <div className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Stammdaten</CardTitle>
              <CardDescription>
                {canWrite ? 'Änderungen werden sofort gespeichert.' : 'Nur Lesen.'}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form className="space-y-6" onSubmit={(event) => void onSave(event)}>
                <AssetFields
                  values={values}
                  lookups={lookups.data}
                  currentDepartment={asset.department}
                  disabled={!canWrite}
                  onChange={patch}
                />
                {canWrite && (
                  <Button type="submit" disabled={updateAsset.isPending}>
                    {updateAsset.isPending && <Loader2Icon className="animate-spin" aria-hidden />}
                    Speichern
                  </Button>
                )}
              </form>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Ticket-Historie</CardTitle>
              <CardDescription>Tickets, die dieses Asset referenzieren.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {asset.tickets.length === 0 ? (
                <p className="text-sm text-muted-foreground">Noch kein Ticket verknüpft.</p>
              ) : (
                <ul className="space-y-3">
                  {asset.tickets.map((ticket) => (
                    <li
                      key={ticket.id}
                      className="flex flex-col gap-2 border-b pb-3 last:border-0 sm:flex-row sm:items-center sm:justify-between"
                    >
                      <div className="space-y-1">
                        <Link
                          to={`/helpdesk/tickets/${ticket.number}`}
                          className="font-medium text-primary hover:underline"
                        >
                          #{ticket.number} · {ticket.subject}
                        </Link>
                        <div className="flex flex-wrap gap-2">
                          <StatusBadge status={ticket.status} />
                          <PriorityBadge priority={ticket.priority} />
                          <span className="text-xs text-muted-foreground">
                            {formatDateTime(ticket.createdAt)}
                          </span>
                        </div>
                      </div>
                      {canWrite && (
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          disabled={unlinkTicket.isPending}
                          onClick={() => void unlinkTicket.mutateAsync(ticket.id)}
                        >
                          Lösen
                        </Button>
                      )}
                    </li>
                  ))}
                </ul>
              )}
              {canWrite && (
                <div className="flex flex-col gap-2 sm:flex-row">
                  <div className="flex-1 space-y-1.5">
                    <Label htmlFor="link-ticket">Ticketnummer</Label>
                    <Input
                      id="link-ticket"
                      inputMode="numeric"
                      placeholder="z. B. 42"
                      value={ticketNumber}
                      onChange={(event) => setTicketNumber(event.target.value)}
                    />
                  </div>
                  <Button
                    type="button"
                    className="sm:mt-6"
                    disabled={linkTicket.isPending}
                    onClick={() => void onLink()}
                  >
                    {linkTicket.isPending && <Loader2Icon className="animate-spin" aria-hidden />}
                    Verknüpfen
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        <aside className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Übersicht</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2 text-sm">
              <p>
                <span className="text-muted-foreground">Standort: </span>
                {asset.location ?? '—'}
              </p>
              <p>
                <span className="text-muted-foreground">Abteilung: </span>
                {asset.department?.name ?? '—'}
              </p>
              <p>
                <span className="text-muted-foreground">Nutzer: </span>
                {asset.assignedUser ? (
                  <PersonChip
                    displayName={asset.assignedUser.displayName}
                    profilePhotoUrl={asset.assignedUser.profilePhotoUrl}
                  />
                ) : (
                  '—'
                )}
              </p>
              <p>
                <span className="text-muted-foreground">Serie: </span>
                {asset.serialNumber ?? '—'}
              </p>
              <p>
                <span className="text-muted-foreground">Kaufdatum: </span>
                {formatDate(asset.purchaseDate)}
              </p>
              <p>
                <span className="text-muted-foreground">Garantie: </span>
                <DateExpiryBadge
                  expired={asset.warrantyExpired}
                  label={formatDate(asset.warrantyUntil)}
                />
              </p>
              <p>
                <span className="text-muted-foreground">Wartung: </span>
                <DateExpiryBadge
                  expired={asset.maintenanceExpired}
                  label={formatDate(asset.maintenanceUntil)}
                />
              </p>
              <p>
                <span className="text-muted-foreground">Aktualisiert: </span>
                {formatDateTime(asset.updatedAt)}
              </p>
            </CardContent>
          </Card>

          {canDelete && (
            <Card>
              <CardHeader>
                <CardTitle>Löschen</CardTitle>
                <CardDescription>
                  Ticket-Verknüpfungen werden mit entfernt. Ausmustern ist meist die bessere Wahl.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button type="button" variant="destructive">
                      Asset löschen
                    </Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle>Asset unwiderruflich löschen?</AlertDialogTitle>
                      <AlertDialogDescription>
                        {asset.name} und alle Ticket-Verknüpfungen werden entfernt.
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel>Abbrechen</AlertDialogCancel>
                      <AlertDialogAction onClick={() => void onDelete()}>Löschen</AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              </CardContent>
            </Card>
          )}
        </aside>
      </div>
    </section>
  );
}
